import React from "react";
import { useTranslation } from "react-i18next";
import { PersonFill, CalendarEvent, Clock } from "../icons";
import { Tag } from "lucide-react";
import { useWidgetScroll } from "../context/WidgetScrollContext";
import { LINK_BUTTON } from "../buttons/linkButtonClassNames";
import "../../styles/booking-step-header.scss";

interface BookingInfoInlineProps {
  readonly venueName?: string;
  readonly bookingDate?: string;
  readonly bookingTime?: string;
  readonly covers?: string;
  readonly bookingTypeName?: string;
  readonly stage?: string;
  readonly onChangeDetails?: () => void;
}

const BookingInfoInline: React.FC<BookingInfoInlineProps> = ({
  venueName,
  bookingDate,
  bookingTime,
  covers,
  bookingTypeName,
  stage,
  onChangeDetails,
}) => {
  const { t } = useTranslation();
  const isWidget = useWidgetScroll() !== undefined;
  const coversLabel = Number(covers) === 1 ? t("booking_covers_guest", "Guest") : t("booking_covers_guests", "Guests");

  /* The extra `pe-4` lines the button's right edge up with the step
     indicator above it ("Pre-order · Step 2 of 3"), whose text sits 16px
     further in because BookingStepHeader's right group adds `px-4` on top
     of the header's `p-6`. */
  const changeDetailsClassName = isWidget
    ? `${LINK_BUTTON} gcw:bg-transparent gcw:border-none gcw:py-0 gcw:ps-0 gcw:pe-4 gcw:cursor-pointer`
    : "gcw:text-text-brand-blue gcw:font-semibold gcw:text-body gcw:block gcw:underline gcw:cursor-pointer gcw:bg-transparent gcw:border-none gcw:py-0 gcw:ps-0 gcw:pe-4";

  return (
    <div className="booking-info-inline gcw:ps-6 gcw:pe-6 gcw:pb-6" data-stage={stage}>
      {venueName && (
        /* `venue-name` marks this as the venue's own name, the same marker
           BookingInfoSidebar puts on its heading. A surface that flattens the
           type scale exempts the marker so the name keeps its size — it
           identifies the booking and is the most important thing on the page.
           Inert on a surface that does not flatten. */
        <span className="venue-name gcw:w-full gcw:font-bold gcw:text-2xl gcw:text-text-primary gcw:lg:hidden gcw:pb-2">
          {venueName}
        </span>
      )}
      <div className="booking-info-inline__details">
        {bookingDate && (
          <span className="gcw:text-body gcw:text-dark-grey gcw:flex gcw:items-center gcw:justify-center gcw:gap-2">
            <CalendarEvent size={18} aria-hidden="true" />
            {bookingDate}
          </span>
        )}
        {bookingTime && (
          <span className="gcw:text-body gcw:text-dark-grey gcw:flex gcw:items-center gcw:justify-center gcw:gap-2">
            <Clock size={18} aria-hidden="true" />
            {bookingTime}
          </span>
        )}
        {covers && (
          <span className="gcw:text-body gcw:text-dark-grey gcw:flex gcw:items-center gcw:justify-center gcw:gap-2">
            <PersonFill size={18} aria-hidden="true" />
            {isWidget ? `${covers} ${coversLabel}` : covers}
          </span>
        )}
        {bookingTypeName && (
          <span className="gcw:text-body gcw:text-dark-grey gcw:flex gcw:items-center gcw:justify-center gcw:gap-2">
            <Tag size={18} aria-hidden="true" />
            {bookingTypeName}
          </span>
        )}
      </div>
      {onChangeDetails && (
        <button type="button" onClick={onChangeDetails} className={changeDetailsClassName}>
          {t("booking_change_details", "Change Details")}
        </button>
      )}
    </div>
  );
};

export default BookingInfoInline;
